import type { Map as MapTypes } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { Suspense, lazy } from 'react';
import ReactMapboxGl from 'react-mapbox-gl';
import type { Props } from 'react-mapbox-gl/lib/map';

import {
  accessToken,
  tilesUrl as defaultTilesUrl,
  isWebGlSupported,
} from 'src/utils/mapUtils';

import ErrorBoundary from 'src/error/components/ErrorBoundary';
import { LayersControl } from './components/LayersControl';
import { ZoomControl } from './components/ZoomControl';

const EmptyMap = lazy(
  () => import(/* webpackChunkName: "EmptyMap" */ './components/EmptyMap'),
);

const Map = ReactMapboxGl({ accessToken });

type MapType = Omit<Props, 'style'>;

interface MapboxProps extends MapType {
  onDragStart?: () => void;
  onClick?: () => void;
  tilesUrl?: string;
  isMobile?: boolean;
  showLayersControl?: boolean;
  showZoomControl?: boolean;
  onZoomStart?: () => void;
  onStyleLoad?: (map: MapTypes) => void;
}

/**
 * Общий компонент карты
 */
export const Mapbox = ({
  children,
  isMobile = false,
  tilesUrl = defaultTilesUrl,
  showLayersControl = true,
  showZoomControl = true,
  ...props
}: MapboxProps): JSX.Element => (
  <ErrorBoundary fallback={<EmptyMap isMobile />} showDialog={false}>
    {isWebGlSupported() ? (
      <Map {...props} style={tilesUrl}>
        <>
          {showZoomControl && <ZoomControl isMobile={isMobile} />}
          {showLayersControl && <LayersControl isMobile={isMobile} />}
          {children}
        </>
      </Map>
    ) : (
      <Suspense fallback={null}>
        <EmptyMap isMobile={isMobile} />
      </Suspense>
    )}
  </ErrorBoundary>
);
